import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import commaNumber from 'comma-number'
import PlaceSearchBox from './place_search_box'
import DateSelect from '../common/date_select_component'
import SearchCondition from './search_condition'
import { PRICES, SORT_TYPE } from '../../common/constants/classifications'
import { validate } from './utils'
import './search_form.scss'
import { withNamespaces } from "react-i18next"
import { compose } from 'redux'

export class SearchForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = validate({
      ...props.match.params,
      ...props.query
    })

    this.onPlaceChanged = this.onPlaceChanged.bind(this)
    this.storePrevPrefecture = this.storePrevPrefecture.bind(this)
    this.toggleDestinationOrPrefecture = this.toggleDestinationOrPrefecture.bind(this)
    this.onPrefectureChange = this.onPrefectureChange.bind(this)
    this.onChangeValue = this.onChangeValue.bind(this)
    this.submitSearch = this.submitSearch.bind(this)

    this.today = moment()
    this.today.locale(this.props.lang)
  }

  get dateFormat() {
    return (this.props.lang === 'en') ? 'ddd MM/DD/YYYY' : 'YYYY年M月D日(ddd)'
  }

  get checkin() {
    const { checkin } = this.state
    return checkin ? moment(checkin, ['YYYY-MM-DD', 'MM/DD/YYYY', 'YYYY/MM/DD']).format('YYYY-MM-DD') : null
  }

  get checkout() {
    const { checkout } = this.state
    return checkout ? moment(checkout, ['YYYY-MM-DD', 'MM/DD/YYYY', 'YYYY/MM/DD']).format('YYYY-MM-DD') : null
  }

  componentDidMount() {
    this.toggleDestinationOrPrefecture()
  }

  componentDidUpdate({ query, match }) {
    if (JSON.stringify(query) !== JSON.stringify(this.props.query)) {
      this.setState(
        validate({
          ...this.props.match.params,
          ...this.props.query
        })
      )
    }
  }

  isBlank(value) {
    return value === undefined || value === null || value === ''
  }

  hasDestinationSelection(query = this.queryFromState) {
    return ![query.lat, query.lon, query.radius].some((value) => this.isBlank(value))
  }

  requiresDestinationSelection(query = this.queryFromState) {
    return !this.isBlank(query.destination) && !this.hasDestinationSelection(query)
  }

  clearDestinationSelectionErrorIfNeeded(nextState) {
    if (this.isBlank(nextState.destination) || this.hasDestinationSelection(nextState)) {
      return false
    }

    return this.state.destinationSelectionError
  }

  /**
   * 目的地サジェストの選択イベントをハンドルする
   */
  onPlaceChanged(place) {
    const nextState = {
      destination: place.name,
      lat: place.lat,
      lon: place.lon,
      radius: place.radius,
      sort: SORT_TYPE.DISTANCE_ASC
    }

    this.setState({
      ...nextState,
      destinationSelectionError: this.clearDestinationSelectionErrorIfNeeded(nextState)
    })
  }

  /**
   * 目的地フォーカス時に現在の prefecture を prevPrefecture に保持する
   */
  storePrevPrefecture() {
    const { prefecture, prevPrefecture } = this.state
    if (!prevPrefecture) {
      this.setState({
        prefecture: undefined,
        prevPrefecture: prefecture
      })
    }
  }

  /**
   * 目的地が空になったら前に選択されていた都道府県を復活させる
   */
  toggleDestinationOrPrefecture() {
    const { destination, prefecture, prevPrefecture } = this.state
    if (!destination) {
      this.setState({
        prefecture: prevPrefecture || prefecture || undefined,
        prevPrefecture: undefined,
        lat: undefined,
        lon: undefined,
        radius: undefined,
        destinationSelectionError: false
      })
    } else {
      this.setState({
        prefecture: undefined
      })
    }
  }

  onChangeValue(e) {
    const { name, value } = e.target
    this.setState({ [name]: value || undefined })
  }

  /**
   * 都道府県のチェンジイベントをハンドルする
   */
  onPrefectureChange(e) {
    this.setState({
      destination: undefined,
      lat: undefined,
      lon: undefined,
      radius: undefined,
      prefecture: e.target.value,
      destinationSelectionError: false
    })
  }

  /**
   * 宿泊料金上限or下限の選択肢の取得する
   */
  getPriceOptionNodes(key) {
    const priceMin = this.state.price_min || 0
    const priceMax = this.state.price_max || Infinity
    const prices = (
      key === 'price_min'
        ? ['', ...PRICES.filter(price => price < Number(priceMax))]
        : ['', ...PRICES.filter(price => price > Number(priceMin))]
    )
    const { t } = this.props

    return prices.map((price) => {
      let label = ''
      if (key === 'price_min' && price === '') {
        label = t('search.search_conditions.filter.min_price.no_minimum')
      } else if (key === 'price_max' && price === '') {
        label = t('search.search_conditions.filter.max_price.no_maximum')
      } else {
        label = `¥${commaNumber(price)}`
      }
      return <option value={price} key={price}>{label}</option>
    })
  }

  /**
   * 目的地や都市名のNodeをrenderする
   */
  renderDestinationNode() {
    const { destination, destinationSelectionError } = this.state
    const { t } = this.props
    return (
      <div styleName="form-groups">
        <dl styleName="form-group">
          <dt styleName="form-group-title">{ t('search.search_conditions.filter.destination.title') }</dt>
          <dd styleName="form-group-input">
            <PlaceSearchBox
              destination={destination || ''}
              onPlaceChanged={this.onPlaceChanged}
              onFocus={this.storePrevPrefecture}
              onBlur={this.toggleDestinationOrPrefecture}
              onInputValueChange={this.onChangeValue}
              countryCode={this.props.countryCode}
              ariaInvalid={destinationSelectionError}
            />
            {destinationSelectionError &&
              <p
                styleName="form-group-error"
                role="alert"
              >
                {t('search.search_conditions.filter.destination.selection_required')}
              </p>
            }
          </dd>
        </dl>
      </div>
    )
  }

  /**
   * チェックイン・チェックアウト input をrenderする
   */
  renderCheckInOutDateNode() {
    const checkoutMin = moment(this.checkin || this.today).add(1, 'd').format('YYYY-MM-DD')
    const { t } = this.props
    if (this.props.isSP) {
      return (
        <div styleName="form-groups">
          <dl styleName="form-group">
            <dt styleName="form-group-title">{ t('search.search_conditions.filter.checkin.title') }</dt>
            <dd styleName="form-group-input">
              <input
                type="date"
                name="checkin"
                defaultValue={this.checkin}
                min={moment(this.today).format('YYYY-MM-DD')}
                onChange={this.onChangeValue}
                placeholder={ t('search.search_conditions.filter.checkin.title') }
                styleName={this.checkin ? 'has-value' : ''}
              />
            </dd>
          </dl>
          <dl styleName="form-group">
            <dt styleName="form-group-title">{ t('search.search_conditions.filter.checkout.title') }</dt>
            <dd styleName="form-group-input">
              <input
                type="date"
                name="checkout"
                defaultValue={this.checkout}
                min={checkoutMin}
                onChange={this.onChangeValue}
                placeholder={ t('search.search_conditions.filter.checkout.title') }
                styleName={this.checkout ? 'has-value' : ''}
              />
            </dd>
          </dl>
        </div>
      )
    }
    return (
      <div styleName="form-groups">
        <dl styleName="form-group">
          <dt styleName="form-group-title">{ t('search.search_conditions.filter.checkin.title') }</dt>
          <dd styleName="form-group-input">
            <DateSelect
              virtical
              checkin={this.checkin}
              checkout={this.checkout}
              startDatePlaceholderText={ t('search.search_conditions.filter.checkin.title') }
              endDatePlaceholderText={ t('search.search_conditions.filter.checkout.title') }
              onUpdate={(value) => { this.setState(value) }}
              onFocusChange={(focusedInput) => {
                if (focusedInput === 'endDate') {
                  // チェックアウト押下時にdatepickerの位置を下げる
                  setTimeout(() => {
                    const picker = document.querySelector('.DateRangePicker_picker')
                    picker.style.top = `${126}px`
                  }, 0)
                }
              }}
              displayFormat={this.dateFormat}
              lang={languageHelper.pageLocale}
            />
          </dd>
        </dl>
      </div>
    )
  }

  /**
   * 宿泊者の人数セレクトをrenderする
   */
  renderGuestSelectNode() {
    const { t } = this.props
    const { adults } = this.state
    const options = [...Array(15)].map((_, i) => {
      if (i === 0) {
        return <option value="" key={i + 1}>{`${i + 1}${t('search.search_conditions.filter.guest.unit')}`}</option>
      }
      return <option value={i + 1} key={i + 1}>{`${i + 1}${t('search.search_conditions.filter.guest.unit')}`}</option>
    })

    return (
      <div styleName="form-groups">
        <dl styleName="form-group">
          <dt styleName="form-group-title">{ t('search.search_conditions.filter.guest.title') }</dt>
          <dd styleName="form-group-select">
            <select
              name="adults"
              value={adults}
              onChange={this.onChangeValue}
            >
              {options}
            </select>
          </dd>
        </dl>
      </div>
    )
  }

  /**
   * 宿泊料金上限（１泊）をrenderする
   */
  renderPriceMinMaxNode() {
    const { price_max: priceMax, price_min: priceMin } = this.state
    const { t } = this.props
    return (
      <div styleName="form-groups">
        <dl styleName="form-group">
          <dt styleName="form-group-title">{ t('search.search_conditions.filter.min_price.title') }</dt>
          <dd styleName="form-group-select">
            <select
              id="price_min"
              name="price_min"
              value={priceMin || ''}
              onChange={this.onChangeValue}
            >
              {this.getPriceOptionNodes('price_min')}
            </select>
          </dd>
        </dl>
        <dl styleName="form-group">
          <dt styleName="form-group-title">{ t('search.search_conditions.filter.max_price.title') }</dt>
          <dd styleName="form-group-select">
            <select
              id="price_max"
              name="price_max"
              value={priceMax || ''}
              onChange={this.onChangeValue}
            >
              {this.getPriceOptionNodes('price_max')}
            </select>
          </dd>
        </dl>
      </div>
    )
  }

  renderSummary() {
    const {
      query: {
        checkin,
        checkout,
        adults,
        price_min: priceMin,
        price_max: priceMax
      },
      place: { value },
      t
    } = this.props

    const checkinLabel = checkin ? moment(checkin).format(this.dateFormat) : t('search.sp.unspecified')
    const checkoutLabel = checkout ? moment(checkout).format(this.dateFormat) : t('search.sp.unspecified')

    return (
      <div styleName="search-form-summary">
        <dl styleName="search-form-summary-list">
          <dt styleName="search-form-summary-title">{t('search.sp.schedule')}:</dt>
          <dd styleName="search-form-summary-value">
            <span>{checkinLabel}</span>
            <span className="notranslate"> - </span>
            <span>{checkoutLabel}</span>
            {checkin && checkout &&
              <React.Fragment>
                <span className="notranslate">
                  {` [${moment(checkout).diff(checkin, 'days')}`}
                </span>
                <span>{t('search.sp.night', { count: moment(checkout).diff(checkin, 'days') })}</span>
                <span className="notranslate">
                  ]
                </span>

              </React.Fragment>
            }
          </dd>
        </dl>
        <dl styleName="search-form-summary-list">
          <dt styleName="search-form-summary-title">{t('search.sp.lodging')}:</dt>
          <dd styleName="search-form-summary-value" className="notranslate">{value}</dd>
        </dl>
        <dl styleName="search-form-summary-list">
          <dt styleName="search-form-summary-title">{t('search.sp.number_of_people')}:</dt>
          <dd styleName="search-form-summary-value"><span className="notranslate">{adults || 1}</span><span>{t('search.search_conditions.filter.guest.unit')}</span></dd>
        </dl>
        <dl styleName="search-form-summary-list">
          <dt styleName="search-form-summary-title">{t('search.sp.fees')}:</dt>
          <dd styleName="search-form-summary-value">
            <span>{priceMin ? `¥${commaNumber(priceMin)}` : t('search.search_conditions.filter.min_price.no_minimum')}</span>
            <span> - </span>
            <span>{priceMax ? `¥${commaNumber(priceMax)}` : t('search.search_conditions.filter.max_price.no_maximum')}</span>

          </dd>
        </dl>
      </div>
    )
  }

  get queryFromState() {
    const {
      destination,
      lat,
      lon,
      radius,
      prefecture,
      checkin,
      checkout,
      adults,
      sort,
      price_min: priceMin,
      price_max: priceMax,
      property_types: propertyTypes,
      amenities: amenitiesList,
      house_rules: houseRules,
      room_types: roomTypes,
      coupon_id: couponId,
      disinfection_measure_codes: disinfectionMeasureCodes,
      instant
    } = this.state

    return {
      destination,
      lat,
      lon,
      radius,
      prefecture,
      checkin,
      checkout,
      adults,
      sort,
      price_min: priceMin,
      price_max: priceMax,
      property_types: propertyTypes,
      amenities: amenitiesList,
      house_rules: houseRules,
      room_types: roomTypes,
      coupon_id: couponId,
      disinfection_measure_codes: disinfectionMeasureCodes,
      instant
    }
  }

  onExtraConditionChange = (state) => {
    const nextQuery = { ...this.queryFromState, ...state }
    const requiresDestinationSelection = this.requiresDestinationSelection(nextQuery)

    this.setState({
      ...this.state,
      ...state,
      destinationSelectionError: !this.props.isSP && requiresDestinationSelection
        ? true
        : this.clearDestinationSelectionErrorIfNeeded(nextQuery)
    })

    if (!this.props.isSP) {
      if (requiresDestinationSelection) {
        return
      }

      this.props.onSubmitForm(nextQuery)
    }
  }

  submitSearch(e) {
    e.preventDefault()

    if (this.requiresDestinationSelection()) {
      this.setState({ destinationSelectionError: true })
      return
    }

    this.setState({ destinationSelectionError: false }, () => {
      this.props.onSubmitForm(this.queryFromState)
    })
  }

  render() {
    const { t } = this.props
    return (
      <aside
        className="search-form-wrap"
        styleName="search-form-wrap"
      >
        {this.renderSummary()}
        <form
          name="form"
          className="search-form"
          styleName="search-form"
          onSubmit={this.submitSearch}
        >
          <div className="primary-conditions" styleName="primary-conditions">
            <div className="search-form-container">
              {/* 目的地や都市名 または 都道府県  */}
              {this.renderDestinationNode()}
              {/* チェックイン・チェックアウト */}
              {this.renderCheckInOutDateNode()}
              {/* 宿泊者の人数 */}
              {this.renderGuestSelectNode()}
              {/* 宿泊料金下限・上限 */}
              {this.renderPriceMinMaxNode()}
            </div>
            <div styleName="search-form-btn-group">
              <button
                type="submit"
                styleName="btn-search"
              >
                <span className="vs-icon-search" styleName="icon-search" />
                <span>{ t('search.search_conditions.filter.button_search.title') }</span>
              </button>
            </div>
          </div>

          {/* こだわり条件 */}
          <SearchCondition
            query={this.props.query}
            coupons={this.props.coupons}
            onChange={this.onExtraConditionChange}
            countryCode={this.props.countryCode}
            t={this.props.t}
            lang={this.props.lang}
          />

          { (this.props.isSP || this.props.isTablet) &&
            <div styleName="search-form-btn-group">
              <button
                type="submit"
                styleName="btn-search"
              >
                <span className="vs-icon-search" styleName="icon-search" />
                <span>{ t('search.search_conditions.filter.button_search.title') }</span>
              </button>
            </div>
          }
        </form>
      </aside>
    )
  }
}

SearchForm.propTypes = {
  lang: PropTypes.string.isRequired,
  isSP: PropTypes.bool.isRequired,
  isTablet: PropTypes.bool.isRequired,
  query: PropTypes.shape({
    adults: PropTypes.string,
    checkin: PropTypes.string,
    checkout: PropTypes.string,
    destination: PropTypes.string,
    instant: PropTypes.string,
    lat: PropTypes.string,
    lon: PropTypes.string,
    radius: PropTypes.string,
    prefecture: PropTypes.string,
    price_max: PropTypes.string,
    price_min: PropTypes.string,
    property_types: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array
    ]),
    room_types: PropTypes.string,
    sort: PropTypes.string
  }),
  match: PropTypes.shape({
    isExact: PropTypes.bool.isRequired,
    params: PropTypes.shape({
      city: PropTypes.string,
      locale: PropTypes.string,
      prefecture: PropTypes.string,
      spot_id: PropTypes.string
    }).isRequired,
    path: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  }).isRequired,
  onSubmitForm: PropTypes.func.isRequired,
  place: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    propertyTypes: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array
    ])
  }).isRequired
}

export default compose( withNamespaces('search') )(SearchForm)
